import { useQuery } from "@tanstack/react-query";
import { PIPELINE_VALUE, REVENUE_BY_DEPARTMENT, REVENUE_HISTORY } from "@/data/mock";
import type { RevenueByDimension } from "@/types";
import { fetchClients } from "./clients";
import { withLatency } from "./simulate";

function aggregateMrrBy(clients: Awaited<ReturnType<typeof fetchClients>>, keyFn: (c: (typeof clients)[number]) => string): RevenueByDimension[] {
  const map = new Map<string, number>();
  for (const c of clients) {
    if (c.status === "churned") continue;
    map.set(keyFn(c), (map.get(keyFn(c)) ?? 0) + c.mrr);
  }
  return [...map.entries()].map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
}

async function computeRevenueOverview() {
  const clients = await fetchClients();
  const liveClients = clients.filter((c) => c.status !== "churned");

  const currentMrr = liveClients.reduce((sum, c) => sum + c.mrr, 0);
  const monthlyTarget = Math.round(currentMrr * 1.06);

  const byAM = aggregateMrrBy(clients, (c) => c.assignedAM).slice(0, 12);
  const byIndustry = aggregateMrrBy(clients, (c) => c.industry);

  const topExpansionOpportunities = clients
    .filter((c) => c.expansionOpportunity)
    .sort((a, b) => (b.expansionValue ?? 0) - (a.expansionValue ?? 0))
    .slice(0, 10);

  const churnRiskClients = clients
    .filter((c) => (c.riskLevel === "critical" || c.riskLevel === "high") && c.status !== "churned")
    .sort((a, b) => b.mrr - a.mrr)
    .slice(0, 12);

  return {
    currentMrr,
    monthlyTarget,
    // No live source yet for pipeline value, historical monthly trend, or department
    // allocation (department isn't a real per-client field) — kept as an illustrative
    // model until a connected revenue-planning source exists.
    pipelineValue: PIPELINE_VALUE,
    history: REVENUE_HISTORY,
    byAM,
    byIndustry,
    byDepartment: REVENUE_BY_DEPARTMENT,
    topExpansionOpportunities,
    churnRiskClients,
  };
}

export async function fetchRevenueOverview() {
  const data = await computeRevenueOverview();
  return withLatency(data, 100);
}

export function useRevenueOverview() {
  return useQuery({ queryKey: ["revenue-overview"], queryFn: fetchRevenueOverview, staleTime: 60_000 });
}
